import { getFieldTypeWidth, STANDARD_COLUMN_WIDTHS } from '../column-widths'
import { Contact } from '../contact'
import {
  DEFAULT_CONTACT_LIFECYCLE_STAGES,
  DEFAULT_CONTACT_VENDOR_STAGES,
} from '../crm-stage'
import { FieldTypeEnum } from '../custom-field.types'
import { ModuleColumnDefinition, ModuleTypeEnum } from '../module'
import { EntityTerminology } from '../organization-settings'

/**
 * Standard column definitions for the Contact module
 */
export const CONTACT_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'firstName',
    name: 'firstName' as keyof Contact,
    label: 'First name',
    description: 'First name of the contact',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'lastName',
    name: 'lastName' as keyof Contact,
    label: 'Last name',
    description: 'Last name of the contact',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 1,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'lifecycleStage',
    name: 'lifecycleStage' as keyof Contact,
    label: 'Lifecycle stage',
    description:
      'Relationship lifecycle stage (contact / qualified / customer / churned). Org-customizable.',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
    // Defaults; actual options are fetched from CrmStage (moduleType=CONTACT_LIFECYCLE)
    options: DEFAULT_CONTACT_LIFECYCLE_STAGES.filter(s => !s.isHidden).map(
      stage => ({
        value: stage.id,
        label: stage.name,
        color: stage.color,
      }),
    ),
  },
  {
    id: 'relationshipTypes',
    name: 'relationshipTypes' as keyof Contact,
    label: 'Lifecycle',
    description:
      'Assigned lifecycle tracks and stages. Import format: "Customer: Contact; Vendor: Active Vendor".',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 2.25,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'relationshipTypeKeys',
    name: 'relationshipTypeKeys',
    label: 'Lifecycle type keys',
    description:
      'Import lifecycle type keys, comma-separated. Example: sales_customer,vendor.',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 2.3,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'relationshipStageIds',
    name: 'relationshipStageIds',
    label: 'Lifecycle stage IDs',
    description:
      'Import lifecycle stage ids, comma-separated and aligned with Lifecycle Type Keys. Example: contact,active_vendor.',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 2.35,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'vendorStage',
    name: 'vendorStage' as keyof Contact,
    label: 'Vendor stage',
    description:
      'Vendor-side status of this contact (potential vendor / vendor / …). Org-customizable. Independent of the sales lifecycle.',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 2.5,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
    options: DEFAULT_CONTACT_VENDOR_STAGES.filter(s => !s.isHidden).map(
      stage => ({
        value: stage.id,
        label: stage.name,
        color: stage.color,
      }),
    ),
  },
  {
    id: 'entity',
    name: 'entity' as keyof Contact,
    label: 'Company',
    description: 'Associated company/entity',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.MODULE_SEARCH,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'supplier',
    name: 'supplier' as keyof Contact,
    label: 'Vendor',
    description:
      'Associated vendor/supplier. Import accepts the vendor name and links (or creates) the vendor record.',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 3.5,
    fieldType: FieldTypeEnum.enum.MODULE_SEARCH,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'email',
    name: 'email' as keyof Contact,
    label: 'Email',
    description: 'Email address of the contact',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 4,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'phone',
    name: 'phone' as keyof Contact,
    label: 'Phone',
    description: 'Phone number of the contact',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 5,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'title',
    name: 'title' as keyof Contact,
    label: 'Job title',
    description: 'Job title of the contact',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 6,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'department',
    name: 'department' as keyof Contact,
    label: 'Department',
    description: 'Department of the contact',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 8,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  // Path C: free-text company stash for contacts with no Entity link yet.
  // The plan calls for surfacing this as a chip with a "Promote to Company" CTA;
  // for now it just renders as editable text. UI promotion comes later.
  {
    id: 'companyName',
    name: 'companyName' as keyof Contact,
    label: 'Company (free-text)',
    description:
      'Stash for company name when no Entity is linked yet. Promote to a real Company to unlock Projects, invoicing, etc.',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 9,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  // Path C: assigned salesperson — absorbed from Contact.assignedToId
  {
    id: 'assignedToId',
    name: 'assignedToId' as keyof Contact,
    label: 'Assigned to',
    description: 'User responsible for this person.',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 10,
    fieldType: FieldTypeEnum.enum.USER,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  // Path C: source — absorbed from Contact.source
  {
    id: 'source',
    name: 'source' as keyof Contact,
    label: 'Source',
    description:
      'How this person came into the funnel (referral, web form, event, …).',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 11,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  // Path C: score — absorbed from Contact.score
  {
    id: 'score',
    name: 'score' as keyof Contact,
    label: 'Score',
    description: 'Contact score for prioritization (0-100).',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 12,
    fieldType: FieldTypeEnum.enum.NUMBER,
    ...STANDARD_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'isPrimary',
    name: 'isPrimary' as keyof Contact,
    label: 'Primary contact',
    description: 'Whether this is the primary contact',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 14,
    fieldType: FieldTypeEnum.enum.BOOLEAN,
    ...STANDARD_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'tags',
    name: 'tags' as keyof Contact,
    label: 'Tags',
    description: 'Tags for organizing and categorizing the contact',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 90, // Positioned near end of standard columns, custom fields come after at 91+
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof Contact,
    label: 'Created date',
    description: 'Date contact was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 15,
    fieldType: FieldTypeEnum.enum.DATETIME,
    dateOnly: true,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'updatedAt',
    name: 'updatedAt' as keyof Contact,
    label: 'Last updated',
    description: 'Date contact was last updated',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 16,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
]

/**
 * Helper to combine standard and custom fields
 */
export function getContactColumns(
  customFields: any[] = [],
  visibilityPreferences?: Record<string, boolean>,
): ModuleColumnDefinition[] {
  const standardColumns = CONTACT_STANDARD_COLUMNS.map(column => ({
    ...column,
    isVisible:
      visibilityPreferences?.[column.id] !== undefined
        ? !!visibilityPreferences[column.id]
        : column.isVisible,
  }))

  const customColumns = customFields.map((field, index) => {
    const fieldType =
      (field.fieldName === 'address' ||
        field.settings?.valueType === 'address') &&
      field.targetModule === 'CONTACT'
        ? 'address'
        : field.fieldType
    const widthSettings = getFieldTypeWidth(fieldType)

    // Parse dropdown options from settings if available
    const fieldOptions =
      (field.fieldType === 'DROPDOWN' || field.fieldType === 'MULTISELECT') &&
      field.settings?.options &&
      Array.isArray(field.settings.options)
        ? field.settings.options.map((opt: any) => {
            if (typeof opt === 'string') return { value: opt, label: opt }
            return {
              value: String(opt.value || opt.id || opt.label || ''),
              label: String(opt.label || opt.name || opt.value || ''),
            }
          })
        : undefined

    return {
      id: `custom_${field.fieldName}`,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible:
        visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
          ? !!visibilityPreferences[`custom_${field.fieldName}`]
          : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 91 + index,
      fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
      ...(fieldOptions ? { options: fieldOptions } : {}),
    }
  }) as ModuleColumnDefinition[]

  return [...standardColumns, ...customColumns]
}

export function getContactModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.CONTACT
}

/**
 * Standard contact columns with the org's entity terminology applied to the
 * company/entity column descriptions. Falls back to the default columns.
 */
export function createContactStandardColumns(
  terminology?: EntityTerminology,
): ModuleColumnDefinition[] {
  if (!terminology) return CONTACT_STANDARD_COLUMNS
  const { singular } = terminology
  const Singular = singular.charAt(0).toUpperCase() + singular.slice(1)
  return CONTACT_STANDARD_COLUMNS.map(column => {
    if (column.id === 'entity') {
      return { ...column, description: `Associated company/${singular}` }
    }
    if (column.id === 'companyName') {
      return {
        ...column,
        description: `Stash for company name when no ${Singular} is linked yet. Promote to a real Company to unlock Projects, invoicing, etc.`,
      }
    }
    return column
  })
}
